/**
 * The api's answer to "was this send the account's first transfer?", named by the send path that heard it, separate
 * from anyone's decision to record it (kitecorp/transferify-ui#3419).
 *
 * Owner directive, 2026-08-27: business logic emits events, telemetry subscribes. The answer (`wasFirst`, the
 * server's atomic marker, api#647) arrives on a different response for each way a transfer is sent: the multipart
 * completion and a continued upload's listing for a hosted upload, the registration for a peer-to-peer send, and the
 * create for a new link to stored files. Each path names the answer here and moves on, without knowing who listens;
 * `analytics/firstTransferTelemetry` is the one subscriber that reports it.
 *
 * A module singleton, like `home/sendEvents`: the send paths live in lazily loaded modules, while the subscriber is
 * wired once on the app shell, which outlives them all.
 *
 * React-free by mandate (`logic-outside-react`).
 */

/**
 * The way a transfer was sent: a hosted upload (always encrypted), a peer-to-peer registration, or a new link to
 * files the account already stores, which uploads nothing.
 */
export type SendPath = "encrypted" | "instant" | "stored";

/** One answer from the api: which path sent the transfer, and whether it was the account's first. */
export interface FirstTransferAnswer {
	path: SendPath;
	wasFirst: boolean;
}

export type FirstTransferListener = (answer: FirstTransferAnswer) => void;

export type Unsubscribe = () => void;

/** The seam the send paths hold: the one thing they say about firstness, and a way for others to listen. */
export interface FirstTransferEvents {
	/** The api answered a send of `path`; `wasFirst` is its answer, passed on exactly as it came. */
	answered(wasFirst: boolean, path: SendPath): void;
	subscribe(listener: FirstTransferListener): Unsubscribe;
}

export function createFirstTransferEvents(): FirstTransferEvents {
	const listeners = new Set<FirstTransferListener>();

	return {
		answered(wasFirst, path) {
			// Iterated over a copy so a listener may unsubscribe from inside its own callback without skipping the
			// listener that follows it.
			[...listeners].forEach(listener => listener({path, wasFirst}));
		},
		subscribe(listener) {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
}

/** The app's emitter. */
export const firstTransferEvents: FirstTransferEvents = createFirstTransferEvents();
